/** @format */
import { css } from 'styled-components';

import { largeTablet, smallTablet } from '@toucan/client/global/application';
import { theme } from '@toucan/client/global/theme';

/* eslint-disable @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */
export const styles = css`
    /*
     * Nothing above: what this needs there depends entirely on what it is under, and
     * that differs per page — the model page's listings already leave 7rem below
     * their "See more" button, the product page's details leave nothing at all — so
     * each view sets its own .model-journey top margin. Below is the same on both,
     * and is this section's to own: the model overview it sits on top of has no top
     * spacing of its own, so without a margin here the two surfaces meet.
     *
     * The gap either side has to be margin rather than padding, and the padding
     * inside kept even, because the section paints its own surface on a grey page:
     * its edges are visible, so uneven padding would show as a lopsided white border
     * rather than as space between it and its neighbors.
     */
    margin-bottom: var(--layout-spacing-48);
    padding: var(--layout-spacing-48) 0;
    background-color: var(--colors-surface-primary);

    /*
     * No space of its own below it. The track that follows already opens with a
     * generous top padding for the fanned cards to rise into, and the paragraph
     * explaining the section was being skipped for sitting too far from the thing
     * it explains — readers went straight to the timeline. Sitting right on top of
     * the cards, it reads as their caption rather than as a preamble to scroll
     * past. Below large tablet the fan's clearance goes, so the gap is put back
     * there explicitly.
     */
    .fan-section-header {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--layout-spacing-8);
        text-align: left;
    }

    .fan-section-eyebrow-row {
        display: flex;
        align-items: center;
        gap: var(--layout-spacing-12);
    }

    /*
     * Set in capitals as a signpost rather than as a sentence, which is the one
     * thing this section's typography helpers deliberately do not do — hence
     * Feather's caption straight from the theme here. Kit intelligence
     * introduces itself with the same rule.
     */
    .fan-section-eyebrow {
        ${theme.typography.caption_highlight}
        text-transform: uppercase;
        color: var(--colors-text-tertiary);
    }

    /*
     * Feather's H2 ships with 24 of its own margin below it, which the header's
     * flex gap of 8 then adds to — 32 in total between the heading and the
     * paragraph explaining it. Halved to 16 by taking the margin down to 8: the
     * two lines are one introduction and were reading as two separate blocks.
     */
    .fan-section-heading {
        margin-bottom: var(--layout-spacing-8);
    }

    .fan-section-intro {
        ${theme.typography.body_normal}
        color: var(--colors-text-secondary);
        max-width: 64rem;
    }

    /*
     * --journey-timeline__marker-size and --journey-timeline__axis-size are not
     * named for this component because they are TimelineMarker's contract with
     * whatever holds it: the marker reads them from its parent, and its own
     * fallbacks would not line up with this axis. Set here so the marker's
     * circle matches this axis exactly.
     */
    --journey-timeline__marker-size: 6.4rem;
    --journey-timeline__axis-size: 6.4rem;

    --journey-fan__marker-clearance: calc(
        var(--journey-timeline__marker-size) / 2 + var(--layout-spacing-12)
    );

    /*
     * How far the rotation pivot sits from the card, as a proportion of the
     * card's own height — the way a hand of playing cards pivots about the
     * fingers holding it. Read by fan-card.styles.ts. Only the distance is set
     * here; which side of the card it falls on is signed per hand below.
     *
     * This is the knob that sets how far apart the cards spread, independently
     * of how far they tilt: the further away the pivot, the more ground each
     * card covers for the same gentle rotation.
     *
     * Kept deliberately tight, so the cards read as a hand someone is holding
     * rather than a row of cards that happen to be tilted. The cost is that a
     * long name on the upper hand can be covered by the card in front of it:
     * names sit at the end of the card nearest that hand's pivot, which is the
     * end where its cards converge. Drawing a card out makes its name readable
     * again, which is most of what the hover is for.
     */
    --journey-fan__pivot-distance: 320%;

    /* How far a card travels when drawn out of the hand. Signed per hand. */
    --journey-fan__lift-distance: 1.6rem;
    --journey-fan__lift-selected-distance: 2.4rem;

    /*
     * Cards stop short of the axis rather than resting on it — a card sitting
     * on the line reads as crossing it, and the line is what the whole section
     * is about.
     */
    --journey-fan__axis-clearance: 2.4rem;

    .fan-section-track {
        position: relative;
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
        align-items: stretch;
        gap: var(--layout-spacing-16);
        /*
         * Rotated cards extend well outside the box that holds them, upward and
         * to either side of the hand, and overflow stays visible at every level
         * so nothing about the fan itself is clipped.
         *
         * Kept tight, so the paragraph above reads as this timeline's caption
         * rather than as a preamble to scroll past — readers were skipping it at
         * a comfortable clearance. It is only as much as keeps the highest fanned
         * card off the text, so anything that raises the fan — a wider spread, a
         * further pivot, a taller card — has to be checked against it.
         *
         * **It has to stay symmetric.** The axis lines are positioned at top 50%
         * of this element, which is half of its padding box, while the dots
         * and the marker are centred in its content box. The two only coincide
         * while the top and bottom padding match: uneven padding slides the lines
         * off every dot on them by half the difference. The extra room the fan
         * wants below it therefore lives on .fan-section-detail, outside this box
         * entirely.
         */
        padding-top: var(--layout-spacing-16);
        padding-bottom: var(--layout-spacing-16);
        overflow: visible;
    }

    /*
     * Each side is its own 1fr auto 1fr grid, exactly the trick the reused
     * TimelineMarker itself uses: whatever the auto row's height turns out to
     * be, the two 1fr rows either side of it consume the remaining space
     * equally, which keeps the auto row's centre on the same line as every
     * other column's, including the marker's, with nothing measured in
     * JavaScript.
     */
    .fan-section-side {
        display: grid;
        grid-template-rows: 1fr auto 1fr;
        justify-items: center;
        min-width: 0;
        overflow: visible;
    }

    /*
     * A side that renders nothing (helpers/index and FanHand both bail out on
     * an empty hand) leaves its column empty rather than letting the marker
     * drift off centre.
     */
    .fan-section-side--before {
        grid-column: 1;
    }

    .fan-section-marker {
        grid-column: 2;
    }

    .fan-section-side--after {
        grid-column: 3;
    }

    .fan-section-hand {
        overflow: visible;
    }

    /*
     * The two hands are mirrored about the axis: what came before is dealt
     * above the line, what comes after hangs below it.
     *
     * The mirroring is done entirely by flipping the sign of the rotation
     * pivot. The past hand pivots about a point below itself, so its cards
     * splay upward; the future hand pivots about a point above itself, so the
     * identical rotation splays them downward. The lift is signed to match,
     * because drawing a card "out of the hand" means away from the axis on
     * both sides. Each hand is also held clear of the line, so that no card
     * ever crosses the one thing this section is about.
     */
    .fan-section-side--before .fan-section-hand {
        grid-row: 1;
        align-self: end;
        margin-bottom: var(--journey-fan__axis-clearance);

        --journey-fan__pivot-y: var(--journey-fan__pivot-distance);
        --journey-fan__lift: calc(-1 * var(--journey-fan__lift-distance));
        --journey-fan__lift-selected: calc(
            -1 * var(--journey-fan__lift-selected-distance)
        );
    }

    /*
     * 100% - distance puts the pivot the same distance beyond the card's top
     * edge as the past hand's sits beyond its bottom edge, so the two fans are
     * true mirrors of each other rather than merely both curved.
     */
    .fan-section-side--after .fan-section-hand {
        grid-row: 3;
        align-self: start;
        margin-top: var(--journey-fan__axis-clearance);

        --journey-fan__pivot-y: calc(100% - var(--journey-fan__pivot-distance));
        --journey-fan__lift: var(--journey-fan__lift-distance);
        --journey-fan__lift-selected: var(
            --journey-fan__lift-selected-distance
        );
    }

    .fan-section-dot {
        grid-row: 2;
        position: relative;
        width: 1.2rem;
        height: 1.2rem;
        border-radius: var(--effects-radius-full);
        background-color: var(--colors-brand-primary);
        /* A ring of page colour so the dot reads as sitting on the axis, not under it. */
        box-shadow: 0 0 0 0.4rem var(--colors-surface-primary);
    }

    .fan-section-line {
        position: absolute;
        top: 50%;
        height: 0.2rem;
        transform: translateY(-50%);
    }

    .fan-section-line--past {
        left: 0;
        right: calc(50% + var(--journey-fan__marker-clearance));
        background-color: var(--colors-brand-primary);
    }

    /* The path ahead is drawn lighter: it is where the reader has not been. */
    .fan-section-line--future {
        left: calc(50% + var(--journey-fan__marker-clearance));
        right: 0;
        background-color: var(--colors-border-primary);
    }

    /*
     * The labels are taken out of the grid entirely — a column would reserve
     * width at both ends of the track and push the hands inward, which is the
     * one thing these labels must not do.
     *
     * The measure is generous so the longer of the two stays on one line. It
     * costs nothing to give them the room: each sits in the half of the track
     * its own hand is not in, so neither can reach the cards however wide it
     * gets.
     */
    .fan-section-label {
        position: absolute;
        margin: 0;
        max-width: 56rem;
    }

    .fan-section-label--past {
        left: 0;
        top: 50%;
        padding-top: var(--layout-spacing-8);
    }

    .fan-section-label--future {
        right: 0;
        bottom: 50%;
        padding-bottom: var(--layout-spacing-8);
    }

    /*
     * Set in the brand pink rather than in a text colour: these two are the only
     * words on the track, and they are what tells a reader which way the axis
     * runs. The pink separates them from the copy above and from the card text
     * either side of them, neither of which is a signpost.
     */
    .fan-section-label-text {
        ${theme.typography.caption_highlight}
        display: inline-flex;
        align-items: center;
        gap: var(--layout-spacing-4);
        color: var(--colors-text-accent);
    }

    .fan-section-label-text svg {
        width: 1.2rem;
        height: 1.2rem;
        flex-shrink: 0;
    }

    /* The shared arrow assets hard-code their fill, so it has to be overridden. */
    .fan-section-label-text svg path {
        fill: var(--colors-text-accent);
    }

    /*
     * All of the space between the fan and the detail card that is not the
     * track's own 16 of padding: the track's padding has to stay symmetric for
     * the axis lines to sit on the dots (see .fan-section-track), so the room the
     * future hand hangs into is held here instead, where it cannot affect that.
     *
     * Kept to the least that clears the lowest fanned card, for the same reason
     * the space above the fan is: the detail card is what a press of the timeline
     * fills in, so it wants to read as part of the timeline rather than as the
     * next thing down the page.
     */
    .fan-section-detail {
        /* 28 is not on the spacing scale, hence the two tokens that make it. */
        margin-top: calc(var(--layout-spacing-24) + var(--layout-spacing-4));
    }

    /*
     * A fan needs horizontal room to splay into and a phone has none, so below
     * large tablet it is abandoned and the same DOM is re-laid out as a plain
     * vertical list: one column, the axis running down the left, each card
     * beside its own dot. TimelineMarker switches to its own axis-column +
     * left-aligned-text layout at this width, which is the layout the cards are
     * matched to here.
     */
    ${largeTablet(css`
        --journey-timeline__marker-size: 5.6rem;
        --journey-timeline__axis-size: 5.6rem;

        /*
         * The track's own clearance goes with the fan below this width, so the
         * header supplies the gap instead — enough to separate it from the first
         * card, and no more, since being close to the cards is the point.
         */
        .fan-section-header {
            margin-bottom: var(--layout-spacing-16);
        }

        .fan-section-track {
            grid-template-columns: minmax(0, 1fr);
            /*
             * Matched to the overreach each card's axis segment is drawn with,
             * so the segments meet across the row gap instead of leaving the
             * axis dashed.
             */
            gap: var(--layout-spacing-16);
            /*
             * The overspill this padding existed to clear was the fan's; with
             * the fan gone the cards stay inside their own box.
             */
            padding-top: 0;
            padding-bottom: 0;
        }

        /*
         * Back to a plain gap. The extra room it carries above this width is for
         * a fan to hang into, and there is no fan left here.
         */
        .fan-section-detail {
            margin-top: var(--layout-spacing-32);
        }

        /*
         * The axis reads the same way as it does above this width: the path
         * already traveled solid, the path ahead lighter. Each card draws its own segment
         * (fan-card.styles.ts); the side it belongs to supplies the colour.
         */
        .fan-section-side--before {
            --journey-fan__axis-colour: var(--colors-brand-primary);
        }

        .fan-section-side--after {
            --journey-fan__axis-colour: var(--colors-border-primary);
        }

        .fan-section-side--before,
        .fan-section-marker,
        .fan-section-side--after {
            grid-column: 1;
        }

        .fan-section-side {
            grid-template-rows: auto;
            justify-items: stretch;
        }

        /*
         * Each side is a single row once the columns collapse, so both hands
         * come back to row 1. Written at the same specificity as the per-side
         * rules above, which would otherwise outrank this and leave the future
         * hand reaching for a third row that no longer exists.
         *
         * Nothing is mirrored or held clear of an axis here: there is no fan
         * and no axis left at this width, just two lists of cards one after
         * the other, so both sides are laid out identically and fill the page.
         */
        .fan-section-side--before .fan-section-hand,
        .fan-section-side--after .fan-section-hand {
            grid-row: 1;
            align-self: stretch;
            margin-top: 0;
            margin-bottom: 0;
        }

        .fan-section-dot {
            display: none;
        }

        .fan-section-line {
            display: none;
        }

        /*
         * There is no free half to hang a label in once the axis is vertical,
         * so they come back into the flow as ordinary rows, indented past the
         * axis column to line up with the cards.
         */
        .fan-section-label {
            position: static;
            grid-column: 1;
            max-width: none;
            padding-top: 0;
            padding-bottom: 0;
            padding-left: var(--journey-timeline__axis-size);
        }

        /*
         * Both labels are pulled in around the marker rather than left at the two
         * ends of the list: the "owned before" label as the last thing before it,
         * "What they buy next" as the first thing after. At the ends of a long
         * vertical list each one was several cards away from the marker it relates
         * to, and a reader could scroll past the middle of the path without either
         * in view. Around the marker they read as one turning point — cards, what
         * they owned before, you are here, what they buy next, cards — and each
         * arrow points at the group it describes.
         *
         * Done with order rather than by moving the markup: the source order is what
         * the horizontal layout wants, and it is also the order this is read out in,
         * where each label still comes before the hand it introduces. The labels are
         * not focusable, so nothing's tab order is affected.
         */
        .fan-section-side--before {
            order: 1;
        }

        .fan-section-label--past {
            order: 2;
        }

        /*
         * Nothing is drawn against the marker, so its circle sits in a clear break
         * in the axis rather than with a line running into it. Matching the line up
         * to the ring exactly was tried and abandoned: the join has to survive the
         * row heights the labels and cards happen to work out to, and it did not.
         */
        .fan-section-marker {
            order: 3;
        }

        .fan-section-label--future {
            order: 4;
        }

        .fan-section-side--after {
            order: 5;
        }

        /*
         * A quarter turn makes the back-arrow point up the page and the
         * forward-arrow point down it, matching the direction the vertical
         * axis actually runs.
         */
        .fan-section-label-text svg {
            transform: rotate(90deg);
        }
    `)}

    ${smallTablet(css`
        margin-bottom: var(--layout-spacing-32);
        padding: var(--layout-spacing-32) 0;

        .fan-section-detail {
            margin-top: var(--layout-spacing-24);
        }
    `)}
`;
/* eslint-enable @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */
